import { Button } from "@convergence/components";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useIssueTokenMutation } from "../api/issueToken";
import { useAuthContext } from "../components/AuthProvider";
import { Header } from "./LoginOtp";

export function ApiTokens() {
    const { user, isLoading } = useAuthContext();

    if (isLoading) return <div role="status">Loading...</div>;
    if (!user) {
        return <Navigate to="/login?redirect=%2Fmcp" replace />;
    }

    return <TokenGenerator />;
}

function TokenGenerator() {
    const mutation = useIssueTokenMutation();
    const token = mutation.data;
    const mcpUrl = `${import.meta.env.VITE_API_ENDPOINT.replace(/\/$/, "")}/mcp`;
    const claudeConfig = JSON.stringify(
        {
            mcpServers: {
                tinker: {
                    command: "npx",
                    args: [
                        "-y",
                        "mcp-remote",
                        mcpUrl,
                        "--transport",
                        "http-only",
                        "--header",
                        "Authorization:${TINKER_AUTH_HEADER}",
                    ],
                    env: { TINKER_AUTH_HEADER: "Bearer <API_TOKEN>" },
                },
            },
        },
        null,
        2,
    );

    async function copyToken() {
        if (!token) return;
        try {
            await navigator.clipboard.writeText(token.token);
            toast.success("API token copied");
        } catch {
            toast.error(
                "Unable to copy. Select the token and copy it manually.",
            );
        }
    }

    return (
        <div className="min-h-screen bg-white text-black font-body">
            <Header />
            <main className="mx-auto max-w-2xl p-4 sm:p-8">
                <section className="flex flex-col gap-6 border border-black p-6">
                    <h1 className="text-4xl">Connect to MCP</h1>
                    <p>
                        Generate a token to connect your MCP client to Tinker.
                        Tokens are valid for 90 days and use your account’s API
                        permissions.
                    </p>
                    <div>
                        <Button
                            disabled={mutation.isPending}
                            onClick={() => mutation.mutate()}
                        >
                            {mutation.isPending
                                ? "Generating..."
                                : "Generate API token"}
                        </Button>
                    </div>
                    {mutation.isError && (
                        <p role="alert" className="text-red-700">
                            Unable to generate an API token. Please try again.
                        </p>
                    )}
                    {token && (
                        <div className="flex flex-col gap-3" aria-live="polite">
                            <label htmlFor="api-token">Your API token</label>
                            <textarea
                                id="api-token"
                                className="ph-no-capture w-full border border-black p-3 font-mono text-sm break-all"
                                readOnly
                                rows={5}
                                value={token.token}
                                spellCheck={false}
                                onFocus={(event) =>
                                    event.currentTarget.select()
                                }
                            />
                            <p>
                                Expires{" "}
                                <time dateTime={token.expiresAt}>
                                    {new Date(token.expiresAt).toLocaleString()}
                                </time>
                            </p>
                            <div>
                                <Button onClick={copyToken}>Copy token</Button>
                            </div>
                            <p className="text-sm">
                                Copy this token before leaving the page.
                            </p>
                        </div>
                    )}
                    <section className="flex flex-col gap-3 border-t border-black pt-6">
                        <h2 className="text-2xl">Connect your MCP client</h2>
                        <p>Use Streamable HTTP with this server URL:</p>
                        <code className="break-all border border-black p-3 text-sm">
                            {mcpUrl}
                        </code>
                        <p>
                            Set the authorization header using your generated
                            token:
                        </p>
                        <code className="break-all border border-black p-3 text-sm">
                            Authorization: Bearer &lt;API_TOKEN&gt;
                        </code>
                    </section>
                    <section className="flex flex-col gap-3 border-t border-black pt-6">
                        <h2 className="text-2xl">Claude Desktop</h2>
                        <ol className="list-decimal pl-5 space-y-3">
                            <li>
                                Install the current Node.js LTS release from{" "}
                                <a
                                    className="inline underline"
                                    href="https://nodejs.org/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    nodejs.org
                                </a>{" "}
                                so Claude Desktop can run <code>npx</code>.
                            </li>
                            <li>Generate an API token above and copy it.</li>
                            <li>
                                In Claude Desktop, open{" "}
                                <strong>
                                    Settings → Developer → Edit Config
                                </strong>{" "}
                                to edit <code>claude_desktop_config.json</code>.
                            </li>
                            <li>
                                Add the configuration below, replacing{" "}
                                <code>&lt;API_TOKEN&gt;</code> with your token.
                                If you already have MCP servers configured, add
                                the <code>tinker</code> entry to your existing{" "}
                                <code>mcpServers</code> object.
                            </li>
                        </ol>
                        <pre className="max-w-full overflow-x-auto border border-black p-3 text-sm">
                            <code>{claudeConfig}</code>
                        </pre>
                        <p>
                            Save the file and fully quit and reopen Claude
                            Desktop. Enable Tinker in the chat’s tools menu.
                            This configuration uses
                            <code> mcp-remote </code> to send your token to the
                            server. Replace the token in the configuration when
                            it expires.
                        </p>
                        <p>
                            Try: “Use Tinker to set the execution context to my
                            box named &lt;box-name&gt;, then show the current
                            execution context.” Starting a stopped box can take
                            several minutes.
                        </p>
                        <a
                            className="underline"
                            href="https://github.com/geelen/mcp-remote#claude-desktop"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Claude Desktop bridge setup reference
                        </a>
                    </section>
                    <section className="flex flex-col gap-3 border-t border-black pt-6">
                        <h2 className="text-2xl">ChatGPT</h2>
                        <p>
                            ChatGPT connects with OAuth, so it doesn’t need an
                            API token. You sign in to Tinker and approve access
                            when ChatGPT asks.
                        </p>
                        <ol className="list-decimal pl-5 space-y-3">
                            <li>
                                Enable Developer mode in ChatGPT’s Settings →
                                Security and login (if available for your
                                account or workspace).
                            </li>
                            <li>
                                Open ChatGPT’s apps page, use the plus button to
                                create a developer-mode app, and enter the MCP
                                server URL shown above.
                            </li>
                            <li>
                                Select OAuth, sign in to Tinker and allow
                                access, then select the app from the
                                conversation’s Developer mode tools.
                            </li>
                        </ol>
                        <a
                            className="underline"
                            href="https://developers.openai.com/api/docs/guides/developer-mode"
                            target="_blank"
                            rel="noreferrer"
                        >
                            ChatGPT developer mode setup and requirements
                        </a>
                    </section>
                </section>
            </main>
        </div>
    );
}
